// ─── exportRouteSummaryPdf — gera PDF do resumo visual da rota ──────────
//
// Gerado no browser via jsPDF. Não precisa de chamada ao backend.
// Layout A4 portrait com:
//   1. Header (cliente + veículo + datas)
//   2. Resumo (km, duração, custo)
//   3. Timeline vertical com cidades, bandeiras, travessias
//   4. Breakdown de custos
//   5. Footer com observações

import jsPDF from 'jspdf';

function hexToRgb(hex) {
  const clean = (hex || '#94a3b8').replace('#', '');
  const v = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean.padEnd(6, '0').slice(0, 6);
  return [
    parseInt(v.slice(0, 2), 16),
    parseInt(v.slice(2, 4), 16),
    parseInt(v.slice(4, 6), 16),
  ];
}

function formatMoney(v, currency = 'EUR') {
  if (v == null) return '—';
  return `${Number(v).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}
function formatDuration(min) {
  if (min == null || isNaN(min)) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h${m > 0 ? ` ${m}m` : ''}`;
  }
  return `${h}h${m > 0 ? ` ${String(m).padStart(2, '0')}` : ''}`;
}

export function exportRouteSummaryPdf({
  highlights,
  route,
  fuelCost,
  tollCost,
  crossingsCost,
  customCost,
  total,
  currency = 'EUR',
  vehicle,
  driver,
  countryCosts = [],
  countryBars = [],
  qrDataUrl = null,
  trackingUrl = null,
  lang = 'pt',
}) {
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 36;
  let y = M;

  const pt = lang === 'pt';

  // ── HEADER ────────────────────────────────────────────────────────
  pdf.setFillColor(15, 23, 42);
  pdf.rect(0, 0, W, 90, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.text('EcooGPS', M, 35);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.text(pt ? 'Resumo de viagem' : 'Trip summary', M, 50);

  // Top right — orderRef/data
  pdf.setFontSize(8);
  pdf.text(new Date().toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    W - M, 35, { align: 'right' });
  if (route.cargoRef) {
    pdf.setFontSize(10);
    pdf.text(`Ref: ${route.cargoRef}`, W - M, 50, { align: 'right' });
  }

  // Vehicle + driver
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  const vehLine = [vehicle?.plate, vehicle?.label || vehicle?.subType].filter(Boolean).join(' · ');
  pdf.text(vehLine || (pt ? 'Veículo' : 'Vehicle'), M, 78);
  if (driver?.name) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(`${pt ? 'Motorista' : 'Driver'}: ${driver.name}`, W - M, 78, { align: 'right' });
  }
  y = 110;

  // QR Code de tracking — canto superior direito, abaixo do header.
  // Só aparece se a rota está despachada (qrDataUrl preenchido).
  if (qrDataUrl) {
    const qrSize = 70;
    const qrX = W - M - qrSize;
    const qrY = y;
    pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    pdf.setFontSize(6.5);
    pdf.setTextColor(100, 116, 139);
    pdf.setFont('helvetica', 'bold');
    pdf.text(pt ? 'TRACKING' : 'TRACKING', qrX + qrSize / 2, qrY + qrSize + 8, { align: 'center' });
    if (trackingUrl) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(5.5);
      pdf.text(trackingUrl.replace(/^https?:\/\//, ''), qrX + qrSize / 2, qrY + qrSize + 16, { align: 'center' });
    }
  }

  // ── KPIs ───────────────────────────────────────────────────────────
  pdf.setTextColor(15, 23, 42);
  pdf.setFontSize(9);
  const kpis = [
    { label: pt ? 'Distância' : 'Distance', value: `${(route.computedRoute?.distance / 1000 || 0).toFixed(0)} km` },
    { label: pt ? 'Duração' : 'Duration', value: formatDuration(Math.round((route.computedRoute?.duration || 0) / 60)) },
    { label: pt ? 'Custo total' : 'Total', value: formatMoney(total, currency), accent: true },
  ];
  const kpiW = (W - 2 * M) / kpis.length;
  kpis.forEach((k, i) => {
    const x = M + i * kpiW;
    pdf.setFillColor(248, 250, 252);
    pdf.roundedRect(x, y, kpiW - 8, 48, 6, 6, 'F');
    pdf.setTextColor(100, 116, 139);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text(k.label.toUpperCase(), x + 10, y + 14);
    pdf.setTextColor(k.accent ? 22 : 15, k.accent ? 163 : 23, k.accent ? 74 : 42);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(15);
    pdf.text(k.value, x + 10, y + 36);
  });
  y += 64;

  // ── MINI-MAPA TERMAL (barras por país) ────────────────────────────
  // Quando o QR está renderizado à direita, reservamos espaço para não
  // sobrepor. Caso contrário usa a largura toda.
  const reservedRight = qrDataUrl ? 100 : 0;
  if (Array.isArray(countryBars) && countryBars.length > 0) {
    pdf.setTextColor(15, 23, 42);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text(pt ? 'DISTRIBUIÇÃO POR PAÍS' : 'DISTRIBUTION BY COUNTRY', M, y);
    y += 12;
    const barAreaW = W - 2 * M - reservedRight;
    countryBars.forEach((b) => {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(15, 23, 42);
      pdf.text(`${b.cc} · ${b.pct.toFixed(1)}%`, M, y + 4);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 116, 139);
      pdf.text(`${b.km.toFixed(0)} km`, M + barAreaW, y + 4, { align: 'right' });
      y += 6;
      // Background da barra
      pdf.setFillColor(241, 245, 249);
      pdf.roundedRect(M, y, barAreaW, 5, 2, 2, 'F');
      // Preenchimento (cor do país)
      const [r, g, bch] = hexToRgb(b.color);
      pdf.setFillColor(r, g, bch);
      const fillW = barAreaW * (b.pct / 100);
      if (fillW > 0) pdf.roundedRect(M, y, fillW, 5, 2, 2, 'F');
      y += 12;
    });
    y += 6;
  }

  // ── TIMELINE ───────────────────────────────────────────────────────
  pdf.setTextColor(15, 23, 42);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text(pt ? 'ITINERÁRIO' : 'ITINERARY', M, y);
  y += 16;

  // Vertical line + markers
  const lineX = M + 14;
  pdf.setDrawColor(203, 213, 225);
  pdf.setLineWidth(1.2);

  const startY = y;
  highlights.forEach((h, i) => {
    if (y > H - 100) {
      pdf.addPage();
      y = M;
    }
    // Dot
    const dotColor = h.kind === 'origin' ? [16, 185, 129]
      : h.kind === 'destination' ? [239, 68, 68]
      : h.kind === 'border' ? [99, 102, 241]
      : h.kind === 'crossing' ? [14, 165, 233]
      : [148, 163, 184];
    pdf.setFillColor(...dotColor);
    pdf.circle(lineX, y + 4, 4, 'F');

    // Text
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(15, 23, 42);
    let label = h.name;
    if (h.kind === 'origin') label = `🚩 ${h.name}`;
    else if (h.kind === 'destination') label = `🏁 ${h.name}`;
    else if (h.kind === 'crossing') label = `⛴ ${h.name}`;
    else if (h.kind === 'border') label = `🌍 ${h.name}`;
    else if (h.kind === 'service') label = `🔧 ${h.name}`;
    else if (h.kind === 'pickup') label = `📦 ${h.name}`;
    pdf.text(label, lineX + 14, y + 8);

    // Km + duração
    if (h.distKm != null || h.durationMin != null) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(100, 116, 139);
      const km = h.distKm != null ? `${h.distKm.toFixed(0)} km` : '';
      const dur = h.durationMin != null ? formatDuration(h.durationMin) : '';
      pdf.text([km, dur].filter(Boolean).join(' · '), W - M, y + 8, { align: 'right' });
    }
    if (h.cost != null) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(14, 165, 233);
      pdf.text(formatMoney(h.cost, h.currency || 'EUR'), W - M, y + 8, { align: 'right' });
    }

    y += 22;
    // Line entre pontos
    if (i < highlights.length - 1) {
      pdf.setDrawColor(203, 213, 225);
      pdf.line(lineX, y - 14, lineX, y);
    }
  });

  // ── CUSTOS ─────────────────────────────────────────────────────────
  if (y > H - 200) { pdf.addPage(); y = M; }
  y += 10;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(15, 23, 42);
  pdf.text(pt ? 'CUSTOS' : 'COSTS', M, y);
  y += 16;

  const rows = [];
  if (fuelCost > 0) rows.push([pt ? 'Combustível' : 'Fuel', formatMoney(fuelCost, currency)]);
  if (tollCost > 0) rows.push([pt ? 'Portagens' : 'Tolls', formatMoney(tollCost, currency)]);
  if (crossingsCost > 0) rows.push([pt ? 'Travessias / Eurotúnel' : 'Crossings / Eurotunnel', formatMoney(crossingsCost, currency)]);
  if (customCost > 0) rows.push([pt ? 'Custos adicionais' : 'Other costs', formatMoney(customCost, currency)]);

  pdf.setFontSize(10);
  rows.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(71, 85, 105);
    pdf.text(label, M + 6, y + 4);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(15, 23, 42);
    pdf.text(value, W - M, y + 4, { align: 'right' });
    y += 18;
  });

  // Total destacado
  pdf.setDrawColor(15, 23, 42);
  pdf.setLineWidth(0.5);
  pdf.line(M, y, W - M, y);
  y += 6;
  pdf.setFillColor(15, 23, 42);
  pdf.roundedRect(M, y, W - 2 * M, 36, 6, 6, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text(pt ? 'CUSTO TOTAL' : 'TOTAL', M + 16, y + 22);
  pdf.setFontSize(16);
  pdf.text(formatMoney(total, currency), W - M - 16, y + 24, { align: 'right' });
  y += 50;

  // ── COMBUSTÍVEL POR PAÍS ───────────────────────────────────────────
  if (Array.isArray(countryCosts) && countryCosts.length > 0) {
    if (y > H - 120) { pdf.addPage(); y = M; }
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(15, 23, 42);
    pdf.setFontSize(10);
    pdf.text(pt ? 'COMBUSTÍVEL POR PAÍS' : 'FUEL BY COUNTRY', M, y);
    y += 14;
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    countryCosts.forEach(({ name: cname, distKm, costEUR }) => {
      pdf.setTextColor(71, 85, 105);
      pdf.text(`${cname}`, M + 6, y + 4);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`${distKm.toFixed(0)} km`, M + 200, y + 4);
      pdf.setTextColor(15, 23, 42);
      pdf.text(formatMoney(costEUR, currency), W - M, y + 4, { align: 'right' });
      y += 16;
    });
  }

  // ── FOOTER ─────────────────────────────────────────────────────────
  pdf.setFontSize(7);
  pdf.setTextColor(148, 163, 184);
  pdf.text(`EcooGPS · Documento gerado em ${new Date().toLocaleString('pt-PT')} · Os valores apresentados são estimativas baseadas em tarifas correntes.`,
    M, H - 20);

  // Nome do ficheiro
  const orig = (route.origin?.name || 'origem').split(',')[0].trim();
  const dest = (route.destination?.name || 'destino').split(',')[0].trim();
  pdf.save(`Resumo-${orig}-${dest}.pdf`);
}
